import { getAnalyticsService, AnalyticsEvent } from './index';

const CONTRACT_DATE_IDS_LIMIT = 50;

export type ConfirmMethod = 'row' | 'bulk' | 'calendar';

export interface CreateConfirmTrackerOptions {
  confirmMethod: ConfirmMethod;
  selectedRowsCount: number;
  contractDateIds: (number | string)[];
  companyId: number;
}

export interface ConfirmTracker {
  trackClicked(): void;
  trackCompleted(confirmedIds: (number | string)[]): void;
  trackFailed(errorCode: string, errorMessage?: string): void;
}

const buildCompoundIds = (ids: (number | string)[], companyId: number): { ids: string[]; hasOverflow: boolean } => {
  const compoundIds = ids.map(id => `${companyId}:${id}`);
  const hasOverflow = compoundIds.length > CONTRACT_DATE_IDS_LIMIT;
  return {
    ids: compoundIds.slice(0, CONTRACT_DATE_IDS_LIMIT),
    hasOverflow,
  };
};

export const createConfirmTracker = ({
  confirmMethod,
  selectedRowsCount,
  contractDateIds,
  companyId,
}: CreateConfirmTrackerOptions): ConfirmTracker => {
  const idempotencyKey = (crypto as unknown as { randomUUID: () => string }).randomUUID();
  const startTime = Date.now();
  const { ids: compoundIds, hasOverflow } = buildCompoundIds(contractDateIds, companyId);

  const trackClicked = (): void => {
    const service = getAnalyticsService();
    service.trackEvent(
      AnalyticsEvent.ConfirmClicked,
      {
        confirm_method: confirmMethod,
        selected_rows_count: selectedRowsCount,
        contract_date_ids: compoundIds,
        ...(hasOverflow && { has_overflow: true }),
        idempotency_key: idempotencyKey,
      },
      { insertId: idempotencyKey },
    );
  };

  const trackCompleted = (confirmedIds: (number | string)[]): void => {
    const service = getAnalyticsService();
    const { ids: confirmedCompoundIds, hasOverflow: confirmedHasOverflow } = buildCompoundIds(confirmedIds, companyId);
    service.trackEvent(
      AnalyticsEvent.ConfirmCompleted,
      {
        confirm_method: confirmMethod,
        confirmed_rows_count: confirmedIds.length,
        selected_rows_count: selectedRowsCount,
        duration_ms: Date.now() - startTime,
        idempotency_key: idempotencyKey,
        contract_date_ids: confirmedCompoundIds,
        ...(confirmedHasOverflow && { has_overflow: true }),
      },
      { insertId: `${idempotencyKey}_completed` },
    );
  };

  const trackFailed = (errorCode: string, errorMessage?: string): void => {
    const service = getAnalyticsService();
    service.trackEvent(
      AnalyticsEvent.ConfirmFailed,
      {
        confirm_method: confirmMethod,
        selected_rows_count: selectedRowsCount,
        error_code: errorCode,
        ...(errorMessage !== undefined && { error_message: errorMessage.slice(0, 256) }),
        duration_ms: Date.now() - startTime,
        idempotency_key: idempotencyKey,
      },
      { insertId: `${idempotencyKey}_failed` },
    );
  };

  return { trackClicked, trackCompleted, trackFailed };
};
